import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { money, fmtDate } from "@/mechanic/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import WorkOrderDialog from "@/mechanic/components/WorkOrderDialog";
import { ArrowLeft, Pencil, FileText, DollarSign, X, Check } from "lucide-react";

const RO_LABEL = {
  estimate: "Estimate", work_order: "Work Order", invoice: "Invoice", paid: "Paid", closed: "Closed",
};

// Mirror of WorkOrderDialog's legacyToLines so historical WOs (labor_cost /
// labor_hours / parts[]) still render their line items on the detail page.
const legacyToLines = (w) => {
  let labor = (w.labor_lines || []).map((l) => ({ ...l }));
  if (!labor.length) {
    const manual = Number(w.labor_cost || 0), h = Number(w.labor_hours || 0), r = Number(w.labor_rate || 0);
    if (manual > 0) labor = [{ description: "Labor", hours: 1, rate_cost: manual, rate_charge: manual }];
    else if (h > 0) labor = [{ description: "Labor", hours: h, rate_cost: r, rate_charge: r }];
  }
  let parts = (w.parts_lines || []).map((l) => ({ ...l }));
  if (!parts.length && (w.parts || []).length) {
    parts = w.parts.map((p) => ({ name: p.name, part_number: "", qty: 1, unit_cost: Number(p.cost || 0), unit_price: Number(p.cost || 0) }));
  }
  return { labor, parts };
};

export default function WorkOrderDetail() {
  const { woId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [invOpen, setInvOpen] = useState(false);
  const [invForm, setInvForm] = useState({ mark_as: "unpaid", amount_paid: "", method: "", note: "" });

  const { data: wo, refetch } = useQuery({
    queryKey: ["mech-wo-detail", woId],
    queryFn: async () => (await api.get(`/work-orders/${woId}`)).data,
    enabled: !!woId,
  });

  const advance = useMutation({
    mutationFn: (target_state) => api.post(`/work-orders/${woId}/advance`, { target_state: target_state || "" }),
    onSuccess: async () => { await refetch(); toast.success("Status updated"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });
  const postPayment = useMutation({
    mutationFn: (body) => api.post(`/work-orders/${woId}/payments`, body),
    onSuccess: async () => { await refetch(); toast.success("Invoice created"); setInvOpen(false); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Failed"),
  });

  const openInvoice = () => {
    const totalDue = wo?.totals?.total || 0;
    setInvForm({ mark_as: "unpaid", amount_paid: "", method: "", note: "", totalDue });
    setInvOpen(true);
  };

  const generateInvoice = async () => {
    // Ensure the linked invoice exists first — advancing to `invoice` state
    // creates it. Then post whatever payment the operator chose.
    try {
      if (!wo?.linked_invoice) {
        await advance.mutateAsync("invoice");
      }
      if (invForm.mark_as === "paid") {
        const amt = Number(invForm.totalDue || wo?.totals?.total || 0);
        if (amt > 0) {
          await postPayment.mutateAsync({ amount: amt, method: invForm.method || "cash", note: invForm.note || "Paid in full" });
        }
      } else if (invForm.mark_as === "partial") {
        const amt = Number(invForm.amount_paid || 0);
        if (amt > 0) {
          await postPayment.mutateAsync({ amount: amt, method: invForm.method || "cash", note: invForm.note || "Partial payment" });
        }
      } else {
        // unpaid — just ensure invoice exists (already done above)
        await refetch();
        setInvOpen(false);
      }
    } catch (e) {
      // errors already surfaced by mutation onError handlers
    }
  };

  if (!wo) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const { labor: laborLines, parts: partsLines } = legacyToLines(wo);
  const totals = wo.totals || {};
  const inv = wo.linked_invoice;
  const invBalance = inv?.totals?.balance ?? totals.total;
  const invPaid = inv?.totals?.amount_paid || 0;
  const stateLabel = RO_LABEL[wo.ro_state] || "";

  return (
    <div className="p-8 max-w-6xl" data-testid="wo-detail-page">
      <Button variant="ghost" onClick={() => navigate("/mechanic/work-orders")} className="mb-4">
        <ArrowLeft className="h-4 w-4 mr-1" />Back to work orders
      </Button>

      {/* Header */}
      <Card className="p-6 mb-6">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="font-heading text-3xl" data-testid="wo-title">{wo.vehicle_label || "Work Order"}</h1>
              <Badge>{wo.status}</Badge>
              <Badge variant="secondary" data-testid="wo-ro-state">{stateLabel}</Badge>
            </div>
            <div className="text-sm text-muted-foreground space-y-0.5">
              <div>{wo.customer_name || "—"} · {fmtDate(wo.service_date)} · Mileage {wo.mileage || "—"}</div>
              {wo.complaint && <div>Complaint: {wo.complaint}</div>}
              {wo.diagnosis && <div>Diagnosis: {wo.diagnosis}</div>}
            </div>
          </div>
          <div className="flex flex-col gap-2 items-end">
            <div className="grid grid-cols-3 gap-6 text-right">
              <div><div className="text-xs text-muted-foreground uppercase">Total</div><div className="text-xl font-mono-data font-bold">{money(totals.total)}</div></div>
              <div><div className="text-xs text-muted-foreground uppercase">Paid</div><div className="text-xl font-mono-data text-emerald-400">{money(invPaid)}</div></div>
              <div><div className="text-xs text-muted-foreground uppercase">Balance</div><div className={`text-xl font-mono-data font-bold ${invBalance > 0.01 ? "text-amber-400" : "text-emerald-400"}`} data-testid="wo-balance">{money(invBalance)}</div></div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setEditOpen(true)} data-testid="wo-edit-btn"><Pencil className="h-4 w-4 mr-1" />Edit</Button>
              <Button onClick={openInvoice} data-testid="wo-invoice-btn"><FileText className="h-4 w-4 mr-1" />{inv ? "Update invoice" : "Create invoice"}</Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Line items */}
      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <Card className="p-5">
          <h3 className="font-heading text-lg mb-3">Labor</h3>
          {laborLines.length === 0 ? <p className="text-sm text-muted-foreground">No labor lines.</p> : (
            <table className="w-full text-sm">
              <thead className="text-muted-foreground text-xs uppercase"><tr><th className="text-left pb-2">Description</th><th className="text-right pb-2">Hrs</th><th className="text-right pb-2">Rate</th><th className="text-right pb-2">Total</th></tr></thead>
              <tbody>
                {laborLines.map((l, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="py-1.5">{l.description || "—"}</td>
                    <td className="text-right font-mono-data">{Number(l.hours || 0).toFixed(2)}</td>
                    <td className="text-right font-mono-data">{money(l.rate_charge || l.rate_cost)}</td>
                    <td className="text-right font-mono-data">{money(Number(l.hours || 0) * Number(l.rate_charge || l.rate_cost || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card className="p-5">
          <h3 className="font-heading text-lg mb-3">Parts</h3>
          {partsLines.length === 0 ? <p className="text-sm text-muted-foreground">No parts.</p> : (
            <table className="w-full text-sm">
              <thead className="text-muted-foreground text-xs uppercase"><tr><th className="text-left pb-2">Part</th><th className="text-left pb-2">Supplier</th><th className="text-right pb-2">Qty</th><th className="text-right pb-2">Sell</th></tr></thead>
              <tbody>
                {partsLines.map((p, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="py-1.5">{p.name || "—"}{p.part_number ? <span className="text-xs text-muted-foreground"> · {p.part_number}</span> : null}</td>
                    <td className="text-xs text-muted-foreground">{p.supplier || "—"}</td>
                    <td className="text-right font-mono-data">{p.qty}</td>
                    <td className="text-right font-mono-data">{money(Number(p.qty || 0) * Number(p.unit_price || p.unit_cost || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      </div>

      {/* Invoice / payments */}
      {inv && (
        <Card className="p-5 mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-lg">Invoice · {inv.invoice_number}</h3>
            <Badge className={invBalance <= 0.01 ? "bg-emerald-500 text-white" : "bg-amber-500 text-white"} data-testid="invoice-status">
              {invBalance <= 0.01 ? "Paid" : invPaid > 0 ? "Partial" : "Unpaid"}
            </Badge>
          </div>
          {(inv.payments || []).length === 0 ? <p className="text-sm text-muted-foreground">No payments recorded yet.</p> : (
            <ul className="divide-y divide-border border border-border rounded-md">
              {inv.payments.map((p) => (
                <li key={p.id} className="flex justify-between px-3 py-2 text-sm" data-testid={`payment-row-${p.id}`}>
                  <span className="font-mono">{p.date} · {p.method || "—"}{p.note ? ` · ${p.note}` : ""}</span>
                  <span className="font-mono-data">{money(p.amount)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      {/* Edit dialog */}
      <WorkOrderDialog open={editOpen} onOpenChange={setEditOpen} editing={wo} onSaved={() => refetch()} />

      {/* Invoice dialog */}
      <Dialog open={invOpen} onOpenChange={setInvOpen}>
        <DialogContent data-testid="invoice-dialog">
          <DialogHeader>
            <DialogTitle>{inv ? "Update invoice" : "Create invoice"}</DialogTitle>
            <DialogDescription>How should this invoice be marked?</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-sm">Payment status</Label>
              <Select value={invForm.mark_as} onValueChange={(v) => setInvForm((f) => ({ ...f, mark_as: v }))}>
                <SelectTrigger data-testid="invoice-mark-as"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="unpaid">Unpaid — invoice generated, amount owing</SelectItem>
                  <SelectItem value="partial">Partial payment received</SelectItem>
                  <SelectItem value="paid">Paid in full</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="border border-border rounded-md p-3 bg-secondary/20 flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Total due</span>
              <span className="font-mono-data font-bold text-lg">{money(invForm.totalDue || totals.total)}</span>
            </div>

            {invForm.mark_as === "partial" && (
              <div>
                <Label className="text-sm">Amount received *</Label>
                <Input type="number" step="0.01" min="0.01" value={invForm.amount_paid} onChange={(e) => setInvForm((f) => ({ ...f, amount_paid: e.target.value }))} data-testid="invoice-amount-paid" />
              </div>
            )}
            {(invForm.mark_as === "paid" || invForm.mark_as === "partial") && (
              <>
                <div>
                  <Label className="text-sm">Payment method</Label>
                  <Input value={invForm.method} onChange={(e) => setInvForm((f) => ({ ...f, method: e.target.value }))} placeholder="Cash, e-transfer, cheque, card…" data-testid="invoice-method" />
                </div>
                <div>
                  <Label className="text-sm">Note (optional)</Label>
                  <Textarea rows={2} value={invForm.note} onChange={(e) => setInvForm((f) => ({ ...f, note: e.target.value }))} />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInvOpen(false)}><X className="h-4 w-4 mr-1" />Cancel</Button>
            <Button onClick={generateInvoice} disabled={advance.isPending || postPayment.isPending} data-testid="invoice-confirm">
              <Check className="h-4 w-4 mr-1" />
              {advance.isPending || postPayment.isPending ? "Working…" : (inv ? "Save invoice" : "Generate invoice")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
